import { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Copy, RotateCcw, ArrowRight } from 'lucide-react';
import Glyph, { type GlyphName } from '../components/Glyph';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { generateAIResponse } from '../lib/aiClient';
import { toast } from 'sonner';
import ToolSwitcher from '../components/ToolSwitcher';
import { ToolKey, getTool } from '../data/tools';
import CoachMarkdown from '../components/CoachMarkdown';

interface Msg {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
  status?: 'thinking' | 'done' | 'error';
}

const STARTERS: { glyph: GlyphName; label: string }[] = [
  { glyph: 'horizon', label: 'Help me pick what to learn next.' },
  { glyph: 'page', label: 'Explain something I am curious about.' },
  { glyph: 'mirror', label: 'Help me check if an answer I got is true.' },
  { glyph: 'path', label: 'Plan something I want to do this week.' },
];

const THINKING_PHASES = ['Reading your question', 'Drafting a plan', 'Reviewing for clarity'];

export default function AICoachPage() {
  const { user, profile } = useAuth();
  const simple = profile?.reading_level === 'simple';
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [thinkingPhase, setThinkingPhase] = useState(0);
  const [tool, setTool] = useState<ToolKey>(getTool(profile?.preferred_tool).key);
  useEffect(() => {
    if (profile?.preferred_tool) setTool(getTool(profile.preferred_tool).key);
  }, [profile?.preferred_tool]);
  const endRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from('coach_messages')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(50);
      setMessages((data as Msg[]) || []);
    })();
  }, [user]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!sending) {
      setThinkingPhase(0);
      return;
    }
    const id = window.setInterval(() => {
      setThinkingPhase((p) => Math.min(p + 1, THINKING_PHASES.length - 1));
    }, 1100);
    return () => window.clearInterval(id);
  }, [sending]);

  const autosize = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 220) + 'px';
  };

  const send = async (textArg?: string) => {
    const text = (textArg ?? input).trim();
    if (!text || !user || sending) return;
    setSending(true);
    const userMsg: Msg = { role: 'user', content: text };
    setMessages((m) => [...m, userMsg, { role: 'assistant', content: '', status: 'thinking' }]);
    setInput('');
    requestAnimationFrame(() => autosize());
    await supabase.from('coach_messages').insert({ user_id: user.id, role: 'user', content: text });

    try {
      const ctx = messages.slice(-10).map((m) => ({ role: m.role, content: m.content }));
      const persona = simple
        ? `You are a kind, patient AI coach for someone who prefers plain language. Use short sentences, friendly examples, and light vocabulary. Keep answers short. Use markdown: short paragraphs, bullet lists with - for tips, and **bold** for key ideas. End with one clear, encouraging follow-up question. Never dumb down the thinking — only the words.`
        : `You are the user's AI Coach. The user is on the "${profile?.role || 'beginners'}" learning path at ${profile?.experience_level || 'beginner'} level. Be specific, kind, and direct. Use plain language. Format answers with markdown: clear headings (##), bullet lists, **bold** for key ideas, and fenced code blocks where helpful.`;
      const reply = await generateAIResponse({
        prompt: text,
        context: [{ role: 'assistant', content: persona }, ...ctx],
        framework: 'coach',
        tool,
      });
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && last.status === 'thinking') {
          next[next.length - 1] = { role: 'assistant', content: reply, status: 'done' };
        } else {
          next.push({ role: 'assistant', content: reply, status: 'done' });
        }
        return next;
      });
      await supabase.from('coach_messages').insert({ user_id: user.id, role: 'assistant', content: reply });
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && last.status === 'thinking') {
          next.pop();
        }
        return next;
      });
      toast.error((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const regenerate = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUser) {
      setMessages((m) => {
        const lastAssistantIdx = m.map((x) => x.role).lastIndexOf('assistant');
        if (lastAssistantIdx >= 0) {
          const next = [...m];
          next.splice(lastAssistantIdx, 1);
          return next;
        }
        return m;
      });
      void send(lastUser.content);
    }
  };

  return (
    <div className="container-app py-8 h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-500 mb-2">AI Coach</div>
          <h1 className="font-display text-4xl">A patient guide for your learning.</h1>
          <p className="mt-1 text-ink-600">A kind, plain-language coach for the path you are on. Ask a question or pick one to start.</p>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-ink-500">Coaching for</span>
            <ToolSwitcher value={tool} onChange={setTool} />
          </div>
        </div>
      </div>

      <div className="flex-1 card p-5 sm:p-6 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto scrollbar-thin pr-2 space-y-5">
          {messages.length === 0 && <EmptyState onPick={(s) => send(s)} />}

          {messages.map((m, i) =>
            m.role === 'user' ? (
              <UserBubble key={m.id || i} text={m.content} initial={(profile?.full_name || 'U').slice(0, 1).toUpperCase()} />
            ) : (
              <CoachCard
                key={m.id || i}
                message={m}
                isLast={i === messages.length - 1}
                thinkingPhase={thinkingPhase}
                onCopy={() => handleCopy(m.content)}
                onRegenerate={regenerate}
                onFollowUp={(t) => send(t)}
              />
            ),
          )}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
          className="mt-5 flex gap-3 items-end"
        >
          <textarea
            ref={textareaRef}
            className="textarea flex-1 min-h-[52px]"
            placeholder="Ask your coach…"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              autosize();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            rows={1}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            aria-label="Send"
            className="btn-primary disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        <div className="mt-2 text-[11px] text-ink-400">Press Enter to send · Shift+Enter for a new line</div>
      </div>
    </div>
  );
}

function UserBubble({ text, initial }: { text: string; initial: string }) {
  return (
    <div className="flex gap-3 flex-row-reverse animate-fade-up">
      <div className="flex-shrink-0 grid place-items-center font-semibold w-8 h-8 rounded-full bg-ink-900 text-warm-50">
        {initial}
      </div>
      <div className="max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap bg-ink-900 text-warm-50">
        {text}
      </div>
    </div>
  );
}

function CoachCard({
  message,
  isLast,
  thinkingPhase,
  onCopy,
  onRegenerate,
  onFollowUp,
}: {
  message: Msg;
  isLast: boolean;
  thinkingPhase: number;
  onCopy: () => void;
  onRegenerate: () => void;
  onFollowUp: (text: string) => void;
}) {
  const phase = THINKING_PHASES[Math.min(thinkingPhase, THINKING_PHASES.length - 1)];

  const followUps = useMemo(() => {
    if (!message.content) return [] as string[];
    const lines = message.content.split('\n').map((l) => l.trim());
    const candidates = lines.filter((l) => /^(- |\* )?.{8,140}\?$/.test(l));
    const cleaned = candidates
      .map((l) => l.replace(/^[-*]\s+/, '').replace(/^\*\*|\*\*$/g, '').trim())
      .filter((l, idx, arr) => arr.indexOf(l) === idx)
      .slice(0, 3);
    return cleaned;
  }, [message.content]);

  return (
    <div className="flex gap-3 animate-fade-up">
      <div className="flex-shrink-0 grid place-items-center w-8 h-8 rounded-full bg-accent-100 text-accent-700">
        <Glyph name="prompt" size={16} />
      </div>
      <div className="flex-1 rounded-2xl border border-ink-100 bg-warm-50 p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-accent-50 text-accent-700 border border-accent-100">
              <Glyph name="prompt" size={12} /> Coach
            </span>
            {message.created_at && (
              <span className="text-[11px] text-ink-400 tabular-nums">
                {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          {message.status === 'done' && (
            <div className="flex items-center gap-1">
              <button
                onClick={onCopy}
                className="p-1.5 rounded-md text-ink-400 hover:text-ink-700 hover:bg-warm-100 transition"
                aria-label="Copy"
                title="Copy"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              {isLast && (
                <button
                  onClick={onRegenerate}
                  className="p-1.5 rounded-md text-ink-400 hover:text-ink-700 hover:bg-warm-100 transition"
                  aria-label="Regenerate"
                  title="Try again"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {message.status === 'thinking' ? (
          <ThinkingIndicator phase={phase} />
        ) : (
          <CoachMarkdown text={message.content} variant="default" />
        )}

        {message.status === 'done' && followUps.length > 0 && (
          <div className="mt-5 pt-4 border-t border-ink-100">
            <div className="text-[11px] uppercase tracking-[0.18em] mb-2 text-ink-500">
              Suggested follow-ups
            </div>
            <div className="flex flex-wrap gap-2">
              {followUps.map((f, i) => (
                <button
                  key={i}
                  onClick={() => onFollowUp(f)}
                  className="chip-accent hover:bg-accent-100 cursor-pointer transition"
                >
                  <ArrowRight className="w-3 h-3" /> {f}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ThinkingIndicator({ phase }: { phase: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex gap-1.5">
        <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse-soft" />
        <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse-soft" style={{ animationDelay: '0.2s' }} />
        <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse-soft" style={{ animationDelay: '0.4s' }} />
      </div>
      <span className="text-sm text-ink-500">{phase}…</span>
    </div>
  );
}

function EmptyState({ onPick }: { onPick: (s: string) => void }) {
  return (
    <div className="text-center py-10 animate-fade-up">
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-50 grid place-items-center">
        <Glyph name="prompt" size={22} />
      </div>
      <h2 className="font-display text-2xl mb-2">What can I help with?</h2>
      <p className="text-ink-500 mb-5 text-sm">Pick a starter or type your own.</p>
      <div className="flex flex-wrap gap-2 justify-center">
        {STARTERS.map((s) => (
          <button key={s.label} onClick={() => onPick(s.label)} className="chip hover:bg-warm-200">
            <Glyph name={s.glyph} size={14} />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
